// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

// doc-exempt: a copy of AYNI 3D's src/main/ai/text-to-speech.ts; never edit it here (scripts/sync-ayni3d-copy.ts)
/**
 * Text to speech for the assistant's replies (docs/AI_CHATBOT_PLAN.md, phase 5).
 * POST {baseUrl}/audio/speech, OpenAI's shape plus `language`, answered with a
 * WAV (the AYNI speech server, speech-server/) or an MP3 (the AYNI gateway's
 * MeloTTS, English and Spanish). The caller has already spelled numbers out
 * (ai-speakable-text.ts). A language the endpoint does not read comes back as
 * AI_UNAVAILABLE, and the surface reads the reply with the device's own voice.
 */
import type { AiChatErrorCode } from '../../shared/ai-chat-protocol';
import type { AiChatLanguage } from '../../shared/ai-languages';
import { errorForStatus, type ChatEndpoint } from './openai-chat-client';

export const AI_SPEAK_TIMEOUT_MS = 30_000;

export type SpeechResult = { ok: true; audio: Uint8Array } | { ok: false; code: AiChatErrorCode; message: string };

/** A WAV ("RIFF") or an MP3 (an ID3 tag, or an MPEG frame sync). */
export function isSpeechAudio(audio: Uint8Array): boolean {
  if (audio.length < 12) return false;
  const head = String.fromCharCode(...audio.subarray(0, 4));
  return head === 'RIFF' || head.startsWith('ID3') || (audio[0] === 0xff && (audio[1]! & 0xe0) === 0xe0);
}

export function speechUrl(baseUrl: string): string {
  return `${baseUrl.replace(/\/+$/, '')}/audio/speech`;
}

export async function synthesizeSpeech(options: {
  endpoint: ChatEndpoint;
  text: string;
  language: AiChatLanguage;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
}): Promise<SpeechResult> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'audio/wav, audio/mpeg' };
  if (options.endpoint.apiKey) headers.Authorization = `Bearer ${options.endpoint.apiKey}`;
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), options.timeoutMs ?? AI_SPEAK_TIMEOUT_MS);
  try {
    const response = await (options.fetchImpl ?? fetch)(speechUrl(options.endpoint.baseUrl), {
      method: 'POST',
      headers,
      body: JSON.stringify({ model: 'tts-1', input: options.text, language: options.language, response_format: 'wav' }),
      signal: abort.signal
    });
    if (response.status === 404) {
      return { ok: false, code: 'AI_UNAVAILABLE', message: 'This endpoint has no voice; set a speech endpoint.' };
    }
    if (!response.ok) {
      const error = errorForStatus(response.status, await response.text().catch(() => ''));
      return { ok: false, code: error.code, message: error.message };
    }
    const audio = new Uint8Array(await response.arrayBuffer());
    return isSpeechAudio(audio) ? { ok: true, audio } : { ok: false, code: 'AI_FAILED', message: 'The voice did not answer with audio.' };
  } catch {
    if (abort.signal.aborted) return { ok: false, code: 'AI_TIMEOUT', message: 'Speaking took too long.' };
    return { ok: false, code: 'AI_UNAVAILABLE', message: 'The speech endpoint cannot be reached.' };
  } finally {
    clearTimeout(timer);
  }
}
